import { FC } from 'react';

import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { Link, useLocation, useParams } from 'react-router-dom';

import { Icon } from './icons';

const linkColorGenerator = (isAccent: boolean) =>
  isAccent ? 'theme-bg-main theme-text-4' : 'theme-text-main';

// 답례품 섹션도 상단 메뉴처럼 선택 시 브랜드 색으로 꽉 채운다(theme-bg-main).
// 단, 일러스트 아이콘은 색이 박혀있어 어두운 배경에 묻히므로, 선택 시 아이콘만
// 흰색 칩(giftIconChip) 위에 올려 또렷하게 보이도록 한다.
const giftLinkClass = (isAccent: boolean) =>
  `flex items-center space-x-3 rounded-md p-2 ${
    isAccent ? 'theme-bg-main theme-text-4' : 'theme-text-main'
  }`;

const giftSubLinkClass = (isAccent: boolean) =>
  `flex items-center space-x-2 rounded-md px-2 py-1.5 ${
    isAccent ? 'theme-bg-main theme-text-4' : 'theme-text-main'
  }`;

// 선택된 줄에서 아이콘을 흰 배경 칩 위에 올려 색이 묻히지 않게 한다.
const giftIconChip = (isAccent: boolean) =>
  `flex items-center justify-center rounded-md p-1 ${isAccent ? 'bg-white' : ''}`;

const StatPageSideBar: FC = () => {
  const { bugoBrandId } = useParams();
  const { pathname } = useLocation();

  const { userProfile, bugoRole, userBugoBrand, isBugoAgencyAdmin, isAdmin } = useAuth();

  const {
    WreathBig,
    BugoMessage,
    Receipt,
    Register,
    Delivery,
    MoneyBag,
    CashAccount,
    Time,
    Home,
  } = Icon();
  const showGiftEventLog = isBugoAgencyAdmin() || isAdmin();

  const bugoBrand = userBugoBrand;

  return (
    <aside className="sticky top-0 h-screen w-60 shrink-0 overflow-y-auto bg-white p-4">
      {/* 회사 / 유저 정보 */}
      <div className="space-y-2 border-b border-gray-300 py-4 font-bold">
        <h6 className="px-2 text-xs text-gray-500">{bugoBrand?.bugoAgency?.corpName}</h6>
        <h4 className="rounded-md bg-[#F3F6FE] p-3 theme-text-main">
          {bugoRole() === LowerRole.BugoAgencyBranchAdmin
            ? userProfile?.bugoAgencyBranchAdminDetail?.teamName
            : bugoBrand?.bugoAgency?.nickName}{' '}
          관리자
        </h4>
      </div>
      {/* 버튼 리스트 */}
      <div className="space-y-3 py-4 text-xs font-bold">
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/flower-stat`}
            className={`flex items-center space-x-3 rounded-md p-2 ${linkColorGenerator(
              pathname.endsWith('flower-stat'),
            )}`}
          >
            <WreathBig className="m-1" />
            <h5>화환 내역</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/bugo-stat`}
            className={`flex items-center space-x-3 rounded-md p-2 ${linkColorGenerator(
              pathname.endsWith('bugo-stat'),
            )}`}
          >
            <BugoMessage />
            <h5>부고 발송 내역</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/bugo-stat-new`}
            className={`flex items-center space-x-3 rounded-md p-2 ${linkColorGenerator(
              pathname.endsWith('bugo-stat-new'),
            )}`}
          >
            <BugoMessage />
            <h5>부고 발송 내역(발인일)</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/agency-worker-stat`}
            className={`flex items-center space-x-3 rounded-md p-2 ${linkColorGenerator(
              pathname.endsWith('agency-worker-stat'),
            )}`}
          >
            <Register />
            <h5>지도사</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/account`}
            className={`flex items-center space-x-3 rounded-md p-2 ${linkColorGenerator(
              pathname.endsWith('account'),
            )}`}
          >
            <div className="p-1">
              <Receipt
                className={`wh-6 ${pathname.endsWith('account') ? 'fill-white' : ''}`}
              />
            </div>
            <h5>정산 내역</h5>
          </Link>
        </div>
        <div>
          <Link
            to={`/bugo-brand/${bugoBrandId}/flower-price-table`}
            className={`flex items-center space-x-3 rounded-md p-2 ${linkColorGenerator(
              pathname.endsWith('flower-price-table'),
            )}`}
          >
            <WreathBig className="m-1" />
            <h5>화환 단가표</h5>
          </Link>
        </div>
        {showGiftEventLog && (
          <>
            <div className="border-t border-gray-200 pt-3">
              <h6 className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                답례품
              </h6>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-event-log`}
                className={giftLinkClass(pathname.endsWith('gift-event-log'))}
              >
                <div className={giftIconChip(pathname.endsWith('gift-event-log'))}>
                  <Time className="wh-6" />
                </div>
                <h5>답례품 로그</h5>
              </Link>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-delivery-management`}
                className={giftLinkClass(pathname.endsWith('gift-delivery-management'))}
              >
                <div
                  className={giftIconChip(pathname.endsWith('gift-delivery-management'))}
                >
                  <Delivery className="wh-6" />
                </div>
                <h5>배송상품 관리</h5>
              </Link>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-dashboard`}
                className={giftLinkClass(pathname.endsWith('gift-dashboard'))}
              >
                <div className={giftIconChip(pathname.endsWith('gift-dashboard'))}>
                  <Home className="wh-6" />
                </div>
                <h5>수익 대시보드</h5>
              </Link>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-settlement/delivery`}
                className={giftLinkClass(pathname.includes('/gift-settlement'))}
              >
                <div className={giftIconChip(pathname.includes('/gift-settlement'))}>
                  <Receipt className="wh-6" />
                </div>
                <h5>정산 관리</h5>
              </Link>
              <div className="ml-9 mt-0.5 space-y-1 text-sm font-medium">
                <Link
                  to={`/bugo-brand/${bugoBrandId}/gift-settlement/delivery`}
                  className={giftSubLinkClass(
                    pathname.includes('gift-settlement/delivery'),
                  )}
                >
                  <div
                    className={giftIconChip(
                      pathname.includes('gift-settlement/delivery'),
                    )}
                  >
                    <Delivery className="wh-5" />
                  </div>
                  <span>배송형</span>
                </Link>
                <Link
                  to={`/bugo-brand/${bugoBrandId}/gift-settlement/coupon`}
                  className={giftSubLinkClass(
                    pathname.includes('gift-settlement/coupon'),
                  )}
                >
                  <div
                    className={giftIconChip(pathname.includes('gift-settlement/coupon'))}
                  >
                    <CashAccount className="wh-5" />
                  </div>
                  <span>쿠폰형</span>
                </Link>
              </div>
              <Link
                to={`/bugo-brand/${bugoBrandId}/gift-price-table`}
                className={giftLinkClass(pathname.endsWith('gift-price-table'))}
              >
                <div className={giftIconChip(pathname.endsWith('gift-price-table'))}>
                  <MoneyBag className="wh-6" />
                </div>
                <h5>단가표</h5>
              </Link>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

export default StatPageSideBar;
